// @ts-nocheck
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@/components/ui';
import {
  Bounce,
  Flash,
  Heartbeat,
  Jello,
  Pulse,
  RubberBand,
  Shake,
  Swing,
  Wiggle,
} from '@/components/animations/attention';

export default function AttentionPage() {
  const [replayKey, setReplayKey] = useState(0);

  // Re-mounts the one-shot examples so their finite animations play again.
  const replay = () => setReplayKey((k) => k + 1);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Attention Animations</Title>
        <Text c="dimmed" size="sm">
          Looping and one-shot motion effects that draw the eye to a specific
          element — perfect for unread badges, validation errors, save buttons,
          and call-to-action prompts. All effects honour the user's reduced-motion
          preference and render statically when it is enabled.
        </Text>
      </Stack>

      <Group>
        <Button onClick={replay}>Replay one-shot examples</Button>
      </Group>

      {/* Pulse */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Pulse</Title>
            <Text size="sm" c="dimmed">
              A gentle, continuous scale/opacity pulse with scale, ring, and glow
              variants — great for live status dots and notification badges.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Stack gap="xs" align="center">
                <Pulse variant="scale" intensity="medium" duration={1200}>
                  <Badge color="green" size="lg">
                    Live
                  </Badge>
                </Pulse>
                <Text size="xs" c="dimmed">scale</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <Pulse variant="ring" color="#fa5252" duration={1500}>
                  <Badge color="red" size="lg">
                    3 new
                  </Badge>
                </Pulse>
                <Text size="xs" c="dimmed">ring</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <Pulse variant="glow" color="#4dabf7" intensity="strong" duration={1400}>
                  <Badge color="blue" size="lg">
                    Syncing
                  </Badge>
                </Pulse>
                <Text size="xs" c="dimmed">glow</Text>
              </Stack>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Pulse variant="scale" intensity="medium" duration={1200}>
  <Badge color="green" size="lg">Live</Badge>
</Pulse>

<Pulse variant="ring" color="#fa5252" duration={1500}>
  <Badge color="red" size="lg">3 new</Badge>
</Pulse>

<Pulse variant="glow" color="#4dabf7" intensity="strong" duration={1400}>
  <Badge color="blue" size="lg">Syncing</Badge>
</Pulse>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'scale' | 'ring' | 'glow' (default: 'scale')
              <br />
              • intensity - 'subtle' | 'medium' | 'strong' (default: 'medium')
              <br />
              • color - CSS color for ring/glow (default: currentColor)
              <br />
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: true) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Shake */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Shake</Title>
            <Text size="sm" c="dimmed">
              A quick shudder for invalid input or failed actions, with
              horizontal, vertical, and rotate directions.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Directions:</Text>
            <SimpleGrid key={replayKey} cols={{ base: 1, sm: 3 }} spacing="md">
              <Shake direction="horizontal" intensity="medium">
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">horizontal</Text>
                </Card>
              </Shake>
              <Shake direction="vertical" intensity="medium">
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">vertical</Text>
                </Card>
              </Shake>
              <Shake direction="rotate" intensity="strong">
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">rotate</Text>
                </Card>
              </Shake>
            </SimpleGrid>
            <Text size="xs" c="dimmed">
              Use “Replay one-shot examples” above to play these again.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Shake direction="horizontal" intensity="medium">
  <Card variant="outline" p="sm">Invalid password</Card>
</Shake>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • direction - 'horizontal' | 'vertical' | 'rotate' (default: 'horizontal')
              <br />
              • intensity - 'subtle' | 'medium' | 'strong' (default: 'medium')
              <br />
              • duration - Animation duration in ms (default: 500)
              <br />
              • infinite - Loop forever (default: false) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Bounce */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Bounce</Title>
            <Text size="sm" c="dimmed">
              A springy bounce in any of four directions — useful for nudging the
              user toward a scroll indicator or a primary action.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Directions:</Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              <Stack gap="xs" align="center">
                <Bounce direction="up" height={16} duration={1200}>
                  <Badge size="lg" variant="filled">
                    up
                  </Badge>
                </Bounce>
              </Stack>
              <Stack gap="xs" align="center">
                <Bounce direction="down" height={16} duration={1200}>
                  <Badge size="lg" variant="filled">
                    down
                  </Badge>
                </Bounce>
              </Stack>
              <Stack gap="xs" align="center">
                <Bounce direction="left" height={16} duration={1200}>
                  <Badge size="lg" variant="filled">
                    left
                  </Badge>
                </Bounce>
              </Stack>
              <Stack gap="xs" align="center">
                <Bounce direction="right" height={16} duration={1200}>
                  <Badge size="lg" variant="filled">
                    right
                  </Badge>
                </Bounce>
              </Stack>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Bounce direction="up" height={16} duration={1200}>
  <Badge size="lg" variant="filled">up</Badge>
</Bounce>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • direction - 'up' | 'down' | 'left' | 'right' (default: 'up')
              <br />
              • height - Bounce distance in pixels (default: 20)
              <br />
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: true) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Heartbeat */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Heartbeat</Title>
            <Text size="sm" c="dimmed">
              A double-thump scale pulse reminiscent of a heartbeat — ideal for
              "like", favourite, or health-status indicators.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Heartbeat scale={1.2} duration={1300}>
                <Badge color="pink" size="xl" variant="light">
                  ♥ 1.2k
                </Badge>
              </Heartbeat>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Heartbeat scale={1.2} duration={1300}>
  <Badge color="pink" size="xl" variant="light">♥ 1.2k</Badge>
</Heartbeat>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • scale - Peak scale of each beat (default: 1.15)
              <br />
              • duration - Animation duration in ms (default: 1300)
              <br />
              • infinite - Loop forever (default: true) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Flash */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Flash</Title>
            <Text size="sm" c="dimmed">
              Blinks the element by toggling opacity — a strong cue for urgent
              alerts or newly arrived items.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group key={replayKey}>
              <Flash count={3} duration={900}>
                <Badge color="red" size="lg">
                  Action required
                </Badge>
              </Flash>
            </Group>
            <Text size="xs" c="dimmed">
              Use “Replay one-shot examples” above to play it again.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Flash count={3} duration={900}>
  <Badge color="red" size="lg">Action required</Badge>
</Flash>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: false)
              <br />
              • count - Number of flashes when not infinite (default: 1)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* RubberBand */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>RubberBand</Title>
            <Text size="sm" c="dimmed">
              A stretchy squash-and-stretch effect that adds playful emphasis to
              a successful action or a freshly toggled control.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group key={replayKey}>
              <RubberBand duration={1000}>
                <Button>Saved!</Button>
              </RubberBand>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<RubberBand duration={1000}>
  <Button>Saved!</Button>
</RubberBand>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: false) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Jello */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Jello</Title>
            <Text size="sm" c="dimmed">
              A wobbly skew that ripples to rest — a fun way to acknowledge a tap
              or confirm an interaction.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group key={replayKey}>
              <Jello duration={1000}>
                <Badge color="grape" size="xl" variant="light">
                  Wobble me
                </Badge>
              </Jello>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Jello duration={1000}>
  <Badge color="grape" size="xl" variant="light">Wobble me</Badge>
</Jello>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: false) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Swing */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Swing</Title>
            <Text size="sm" c="dimmed">
              Pivots the element from its top edge like a hanging sign — handy for
              notification bells and pendant-style badges.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group key={replayKey}>
              <Swing angle={20} duration={1000}>
                <Badge color="yellow" size="xl" variant="filled">
                  🔔 Reminder
                </Badge>
              </Swing>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Swing angle={20} duration={1000}>
  <Badge color="yellow" size="xl" variant="filled">🔔 Reminder</Badge>
</Swing>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • angle - Peak swing angle in degrees (default: 15)
              <br />
              • duration - Animation duration in ms (default: 1000)
              <br />
              • infinite - Loop forever (default: false) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Wiggle */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Wiggle</Title>
            <Text size="sm" c="dimmed">
              A quick left-right rotation pivoting from the bottom — a subtle nudge
              for icons, hints, or onboarding tooltips.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group key={replayKey}>
              <Wiggle angle={12} duration={500} count={3}>
                <Button variant="light">Need a hand?</Button>
              </Wiggle>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Wiggle angle={12} duration={500} count={3}>
  <Button variant="light">Need a hand?</Button>
</Wiggle>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • angle - Peak rotation in degrees (default: 10)
              <br />
              • duration - Animation duration in ms (default: 500)
              <br />
              • infinite - Loop forever (default: false) / count - Iterations
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Form Validation</Text>
              <Text size="xs" c="dimmed">
                Shake an invalid field on submit, then Flash the error message to
                make the problem impossible to miss.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Live Status &amp; Notifications</Text>
              <Text size="xs" c="dimmed">
                Pulse a "live" dot, Swing a notification bell, or Heartbeat an
                unread badge to signal real-time activity.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Action Confirmation</Text>
              <Text size="xs" c="dimmed">
                Play a one-shot RubberBand or Jello after a save or toggle to give
                tactile, satisfying feedback.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Calls to Action</Text>
              <Text size="xs" c="dimmed">
                Bounce a scroll cue or Wiggle a help button to gently guide the
                user toward the next step.
              </Text>
            </Stack>
          </SimpleGrid>

          <Divider />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Accessibility</Text>
            <Text size="xs" c="dimmed">
              Every component checks the user's reduced-motion preference and
              renders its children statically when motion is reduced. Pass
              active={'{false}'} to disable an effect imperatively, and prefer
              finite count over infinite for non-essential emphasis.
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
